import { z } from 'zod';
import {
	ExpectedTransactionType,
	GetSecurityDataForMipRes,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { preprocessEmptyStringAsUndefined } from '@dealstack/shared/lib/common/common.ts';

export const ALLOCATION_FORM_FIELD = {
	securityId: 'securityId',
	units: 'units',
	cost: 'cost',
	vestingTermId: 'vestingTermId',
	expectedTransactionType: 'expectedTransactionType',
} as const;

export type AllocationFormValues = {
	securityId: string;
	units: number | undefined;
	cost: number | undefined;
	vestingTermId: string | null;
	expectedTransactionType: TransactionType | ExpectedTransactionType | null;
};

export const buildAllocationFormSchema = (tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>) =>
	z.object({
		[ALLOCATION_FORM_FIELD.securityId]: z
			.string()
			.uuid(tCommonUi('investment_allocations.error.security.required')),
		[ALLOCATION_FORM_FIELD.units]: z.preprocess(
			preprocessEmptyStringAsUndefined,
			z
				.number({
					required_error: tCommonUi('investment_allocations.error.units.required'),
					invalid_type_error: tCommonUi('investment_allocations.error.units.required'),
				})
				.min(0, {
					message: tCommonUi('investment_allocations.error.units.nonnegative'),
				})
		),
		[ALLOCATION_FORM_FIELD.cost]: z.preprocess(
			preprocessEmptyStringAsUndefined,
			z
				.number({
					required_error: tCommonUi('investment_allocations.error.cost.required'),
					invalid_type_error: tCommonUi('investment_allocations.error.cost.required'),
				})
				.min(0, tCommonUi('investment_allocations.error.cost.nonnegative'))
		),
		[ALLOCATION_FORM_FIELD.vestingTermId]: z.string().uuid().nullable(),
		[ALLOCATION_FORM_FIELD.expectedTransactionType]: z
			.nativeEnum(ExpectedTransactionType)
			.nullable(),
	});

export type AllocationSubmitValues = z.infer<ReturnType<typeof buildAllocationFormSchema>>;

export const formatSecurityLabel = (security: Pick<GetSecurityDataForMipRes, 'name' | 'type'>) =>
	`${security.name} (${security.type})`;
